<template>
  <page-overlay @close="handleOverlayClose">
    <template slot="title">
      <span>{{ knowledge.name }} · 文档管理</span>
      <a-tag color="blue" class="ref-code-tag">{{ knowledge.refCode }}</a-tag>
      <a-tag :color="statusConfig.color">{{ statusConfig.label }}</a-tag>
    </template>

    <div class="document-management">
      <!-- 知识库信息卡 -->
      <div class="kb-head">
        <div class="kb-ico"><a-icon type="book" /></div>
        <div class="kb-meta">
          <div class="kb-name">{{ knowledge.name }}</div>
          <div class="kb-desc">{{ knowledge.description || "暂无描述" }}</div>
          <div class="kb-cfg">
            <span>TopK：<b>{{ formatNum(knowledge.topK) }}</b></span>
            <span>相似度阈值：<b>{{ thresholdText }}</b></span>
            <span>文档数：<b>{{ table.pagination.total }}</b></span>
          </div>
        </div>
      </div>

      <div class="list-card">
        <a-form layout="inline">
          <a-form-item label="引用编码：">
            <a-input
              v-model.trim="searchForm.refCode"
              placeholder="请输入文档引用编码"
              allow-clear
              class="inputClass"
              @pressEnter="search"
            />
          </a-form-item>
          <span>
            <a-button type="primary" @click="search" style="margin-top: 3px">
              <a-icon type="search" />查询
            </a-button>
            <a-button style="margin-left: 15px" @click="resetSearchForm">重置</a-button>
          </span>
        </a-form>

        <div class="toolbar">
          <a-button type="primary" @click="openDocAddModal">
            <a-icon type="plus" />新建文档
          </a-button>
        </div>

        <a-table
          :rowKey="(record, index) => record.id || index"
          :columns="table.columns"
          :data-source="table.dataInfo"
          :pagination="table.pagination.total ? table.pagination : false"
          :loading="table.loading"
          :scroll="{ x: 1570 }"
          @change="pageChange"
          bordered
          style="margin-top: 15px"
        >
          <span slot="doc" slot-scope="text, record">
            <div class="cell-main">{{ docFileName(record.url) }}</div>
            <div class="cell-sub url" :title="record.url">{{ record.url || "-" }}</div>
          </span>
          <span slot="dataType" slot-scope="text, record">
            <a-tag :color="dataTypeConfig(record.url).color">
              {{ dataTypeConfig(record.url).label }}
            </a-tag>
          </span>
          <span slot="refCode" slot-scope="text">
            <a-tag v-if="text" color="blue" class="ref-code-tag">{{ text }}</a-tag>
            <span v-else>-</span>
          </span>
          <span slot="contentLength" slot-scope="text">{{ formatNum(text) }}</span>
          <span slot="tokens" slot-scope="text">{{ formatNum(text) }}</span>
          <span slot="retrievalCount" slot-scope="text">{{ formatNum(text) }}</span>
          <span slot="status" slot-scope="text">
            <a-tag :color="getStatusConfig(text).color">{{ getStatusConfig(text).label }}</a-tag>
          </span>
          <span slot="processStatus" slot-scope="text">
            <a-tag :color="getProcessStatusConfig(text).color">
              {{ getProcessStatusConfig(text).label }}
            </a-tag>
          </span>
          <span slot="createTime" slot-scope="text">
            <div class="time-cell">{{ formatDate(text) }}</div>
            <div class="time-cell time-sub">{{ formatTime(text) }}</div>
          </span>
          <span slot="action" slot-scope="text, record" class="action">
            <template v-if="record.processStatus === 1">
              <a-switch
                :checked="record.status === 0"
                @change="(checked) => handleStatusChange(record, checked)"
              />
              <a-button class="btnClass" type="link" @click="handleDetail(record)">详情</a-button>
              <a-button class="btnClass" type="link" @click="openSegView(record)">分块</a-button>
              <a-button class="btnClass" type="link" @click="handleEdit(record)">编辑</a-button>
              <a-button class="btnClass delete-btn" type="link" @click="handleDelete(record)">删除</a-button>
            </template>
            <span v-else>-</span>
          </span>
        </a-table>
      </div>
    </div>

    <a-modal
      :maskClosable="false"
      title="新建文档"
      :visible="docModalVisible"
      :confirm-loading="docConfirmLoading"
      :ok-text="docAddType === 'local' ? '开始上传' : '创建文档'"
      width="720px"
      @ok="handleDocAddOk"
      @cancel="handleDocAddCancel"
    >
      <a-form-model
        ref="docForm"
        :model="docForm"
        :rules="docRules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 16 }"
      >
        <a-form-model-item label="文档来源" required>
          <a-radio-group v-model="docAddType" @change="handleSourceTypeChange">
            <a-radio-button value="local">
              <a-icon type="upload" /> 本地文档
            </a-radio-button>
            <a-radio-button value="url">
              <a-icon type="link" /> 网页链接
            </a-radio-button>
          </a-radio-group>
        </a-form-model-item>

        <a-form-model-item v-if="docAddType === 'local'" label="选择文件" required>
          <a-upload-dragger
            :before-upload="beforeDocUpload"
            :show-upload-list="false"
            accept=".pdf,.docx,.txt,.md,.xls,.xlsx,.json"
          >
            <p class="ant-upload-drag-icon"><a-icon type="inbox" /></p>
            <p class="ant-upload-text">点击或拖拽文件到此处上传</p>
            <p class="ant-upload-hint">支持 pdf / docx / txt / md / xls / xlsx / json，单文件不超过 20MB</p>
          </a-upload-dragger>
          <div v-if="uploadFile" class="file-chip">
            <span class="file-chip__type">{{ uploadFileExt }}</span>
            <span class="file-chip__name" :title="uploadFile.name">{{ uploadFile.name }}</span>
            <span class="file-chip__size">{{ uploadFileSizeText }}</span>
            <a-icon type="delete" class="file-chip__remove" @click="removeUploadFile" />
          </div>
          <div v-show="fileError" class="file-error">请选择要上传的文件</div>
        </a-form-model-item>

        <a-form-model-item v-else label="文件 URL" required>
          <a-input
            v-model.trim="docForm.url"
            placeholder="https://example.com/kb/xxx.pdf"
            allow-clear
            @change="urlError = ''"
          />
          <div class="field-hint">服务端将拉取该 URL 的文件内容并完成解析、分块与向量化</div>
          <div v-show="urlError" class="file-error">{{ urlError }}</div>
        </a-form-model-item>

        <a-form-model-item label="文件描述" prop="description">
          <a-textarea
            v-model="docForm.description"
            placeholder="简要说明文件内容或用途，便于后续管理与检索（选填）"
            :rows="2"
            :maxLength="200"
          />
        </a-form-model-item>

        <a-form-model-item :wrapper-col="{ span: 22 }">
          <biz-type-select ref="docBizType" v-model="docForm.refCode"></biz-type-select>
        </a-form-model-item>

        <a-form-model-item label="分片最大 Token 数" prop="segmentMaxTokens">
          <a-input-number
            v-model="docForm.segmentMaxTokens"
            :min="100"
            :max="4000"
            :step="50"
            :precision="0"
            style="width: 100%"
          />
          <div class="field-hint">默认 500；文本将按 token 滑动窗口切分，超长段落句子级滑窗重叠</div>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <a-modal
      :maskClosable="false"
      title="编辑文档"
      :visible="editModalVisible"
      :confirm-loading="editConfirmLoading"
      width="720px"
      @ok="handleEditOk"
      @cancel="handleEditCancel"
    >
      <a-spin :spinning="editLoading">
        <a-form-model
          ref="editForm"
          :model="editForm"
          :rules="editRules"
          :label-col="{ span: 6 }"
          :wrapper-col="{ span: 16 }"
        >
          <a-form-model-item label="文档">
            <div class="cell-main">{{ editForm.fileName || "-" }}</div>
            <div class="cell-sub url" :title="editForm.url">{{ editForm.url || "-" }}</div>
          </a-form-model-item>

          <a-form-model-item label="文件描述" prop="description">
            <a-textarea
              v-model="editForm.description"
              placeholder="简要说明文件内容或用途（选填）"
              :rows="2"
              :maxLength="200"
            />
          </a-form-model-item>

          <a-form-model-item :wrapper-col="{ span: 22 }">
            <biz-type-select ref="editBizType" v-model="editForm.refCode"></biz-type-select>
          </a-form-model-item>

          <a-form-model-item label="分片最大 Token 数" prop="segmentMaxTokens">
            <a-input-number
              v-model="editForm.segmentMaxTokens"
              :min="100"
              :max="4000"
              :step="50"
              :precision="0"
              style="width: 100%"
            />
            <div class="field-hint">修改描述或分片上限会触发文档全量重新分块，请谨慎修改</div>
          </a-form-model-item>
        </a-form-model>
      </a-spin>
    </a-modal>

    <!-- 详情面板：弃用 a-drawer（antd-vue 1.7.8 抽屉动画/滚动锁状态机在此页面
         偶发卡死：面板位移停在关闭值、遮罩不消失、body 滚动锁不释放，导致整页不可点） -->
    <div v-if="detailDrawerVisible" class="detail-drawer">
      <div class="detail-drawer__mask" @click="closeDetailDrawer"></div>
      <div class="detail-drawer__panel">
        <div class="detail-drawer__header">
          <span class="detail-drawer__title">文档详情</span>
          <a-icon type="close" class="detail-drawer__close" @click="closeDetailDrawer" />
        </div>
        <div class="detail-drawer__body">
          <a-spin :spinning="detailLoading">
        <div class="detail-file">
          <span class="detail-file__badge">{{ detailExtBadge }}</span>
          <div class="detail-file__info">
            <div class="cell-main">{{ detail.fileName || "-" }}</div>
            <div class="cell-sub">文档编号 #{{ detail.id || "-" }}</div>
          </div>
        </div>

        <div class="kv-list">
          <div class="kv"><span class="k">所属知识库</span><span class="v">{{ knowledge.name }}（ID: {{ knowledge.id }}）</span></div>
          <div class="kv"><span class="k">引用编码</span><span class="v">{{ detail.refCode || "-" }}</span></div>
          <div class="kv"><span class="k">数据类型</span><span class="v">{{ dataTypeConfig(detail.url).label }}</span></div>
          <div class="kv"><span class="k">文件描述</span><span class="v">{{ detail.description || "—" }}</span></div>
          <div class="kv"><span class="k">文件 URL</span>
            <span class="v">
              <a
                v-if="isHttpUrl(detail.url)"
                :href="detail.url"
                target="_blank"
                rel="noopener noreferrer"
              >{{ detail.url }}</a>
              <template v-else>{{ detail.url || "-" }}</template>
            </span>
          </div>
          <div class="kv"><span class="k">字符数</span><span class="v num">{{ formatNum(detail.contentLength) }}</span></div>
          <div class="kv"><span class="k">Token 数</span><span class="v num">{{ formatNum(detail.tokens) }}</span></div>
          <div class="kv"><span class="k">分片上限</span><span class="v num">{{ detail.segmentMaxTokens }} tokens/片</span></div>
          <div class="kv"><span class="k">召回次数</span><span class="v num">{{ formatNum(detail.retrievalCount) }}</span></div>
          <div class="kv"><span class="k">状态</span><span class="v">{{ detailStatusText }}</span></div>
          <div class="kv"><span class="k">创建时间</span><span class="v num">{{ detail.createTime || "-" }}</span></div>
        </div>

        <div class="detail-content-title">文档内容（分块前原文）</div>
        <div class="detail-content-box">{{ detail.content || "（暂无内容）" }}</div>
          </a-spin>
        </div>
      </div>
    </div>

    <!-- 三级页面：分块管理（覆盖层逐层整盖） -->
    <segment-management
      v-if="segOverlayDoc"
      :doc="segOverlayDoc"
      :knowledge="knowledge"
      @back="closeSegView"
    ></segment-management>
  </page-overlay>
</template>

<script>
import PageOverlay from "@/components/DSComponents/PageOverlay";
import {
  getDocumentPage,
  getDocumentDetail,
  createDocument,
  updateDocument,
  updateDocumentStatus,
  deleteDocument,
  uploadDocument,
} from "@/api/intelligentCustomerService";
import { KNOWLEDGE_STATUS_MAP, DOCUMENT_PROCESS_STATUS_MAP } from "../../help";
import BizTypeSelect from "./BizTypeSelect.vue";
import SegmentManagement from "./SegmentManagement.vue";
import { dataTypeOfUrl, docFileName } from "./docUtils";

const DOC_DATA_TYPE_MAP = {
  doc: { label: "文档", color: "blue" },
  image: { label: "图片", color: "purple" },
  link: { label: "网页链接", color: "green" },
};

// 新建文档：本地文件上传限制（后端分块解析仅支持文档格式，不支持图片/视频）
const DOC_UPLOAD_EXTS = ["pdf", "docx", "txt", "md", "xls", "xlsx", "json"];
const DOC_UPLOAD_MAX_MB = 20;

// ===== 临时 Mock 数据（后端接口就绪后整段删除，含 USE_MOCK 开关与 applyMockList）=====
// 联调完成：接口已对齐，真实请求恢复；Mock 数据保留备查，确认稳定后整段删除
const USE_MOCK = false;

const MOCK_DOC_LIST = [
  { id: 101, knowledgeId: 1, refCode: "park-enterprise", description: "园区企业从签约、注册到开办的全流程操作指南", url: "https://oss.yzkj.com/kb/park/企业注册流程指南.docx", contentLength: 18342, tokens: 9265, segmentMaxTokens: 500, retrievalCount: 128, status: 0, createTime: "2026-06-12 11:02:00" },
  { id: 102, knowledgeId: 1, refCode: "park-enterprise", url: "https://oss.yzkj.com/kb/park/高新技术企业认定政策.pdf", contentLength: 42180, tokens: 21876, segmentMaxTokens: 500, retrievalCount: 87, status: 0, createTime: "2026-06-13 09:45:00" },
  { id: 103, knowledgeId: 1, refCode: "park-enterprise", url: "https://oss.yzkj.com/kb/park/人才落户政策.txt", contentLength: 8602, tokens: 4310, segmentMaxTokens: 500, retrievalCount: 56, status: 0, createTime: "2026-06-15 16:20:00" },
  { id: 104, knowledgeId: 1, refCode: "park-enterprise", url: "https://oss.yzkj.com/kb/park/园区租金减免办法.docx", contentLength: 5210, tokens: 2612, segmentMaxTokens: 500, retrievalCount: 34, status: 1, createTime: "2026-06-18 10:11:00" },
  { id: 105, knowledgeId: 1, refCode: "park-enterprise", url: "https://oss.yzkj.com/kb/park/园区服务大厅导览图.png", contentLength: 486, tokens: 243, segmentMaxTokens: 500, retrievalCount: 12, status: 0, createTime: "2026-06-25 14:12:00" },
  { id: 106, knowledgeId: 1, refCode: "park-enterprise", url: "https://www.yzkj.gov.cn/news/policy/高新技术企业培育政策要点", contentLength: 6800, tokens: 3400, segmentMaxTokens: 500, retrievalCount: 8, status: 0, createTime: "2026-06-28 09:40:00" },
  { id: 107, knowledgeId: 1, refCode: "park-enterprise", url: "https://oss.yzkj.com/kb/park/智慧园区服务平台操作手册.docx", contentLength: 26540, tokens: 13270, segmentMaxTokens: 500, retrievalCount: 76, status: 0, createTime: "2026-07-05 13:26:00" },
  { id: 108, knowledgeId: 1, refCode: "park-enterprise", url: "https://oss.yzkj.com/kb/park/企业社保开户指引.pdf", contentLength: 9120, tokens: 4560, segmentMaxTokens: 500, retrievalCount: 23, status: 0, createTime: "2026-07-11 10:05:00" },
  { id: 109, knowledgeId: 1, refCode: "park-enterprise", url: "https://oss.yzkj.com/kb/park/知识产权申报流程指南.md", contentLength: 4380, tokens: 2190, segmentMaxTokens: 500, retrievalCount: 9, status: 1, createTime: "2026-07-16 15:48:00" },
  { id: 110, knowledgeId: 1, refCode: "park-enterprise", url: "https://oss.yzkj.com/kb/park/惠企政策汇编2026.pdf", contentLength: 65300, tokens: 32650, segmentMaxTokens: 800, retrievalCount: 156, status: 0, createTime: "2026-07-22 09:18:00" },
  { id: 111, knowledgeId: 1, refCode: "park-enterprise", url: "https://oss.yzkj.com/kb/park/园区全景导览图.jpg", contentLength: 652, tokens: 326, segmentMaxTokens: 500, retrievalCount: 7, status: 0, createTime: "2026-08-01 11:34:00" },
  { id: 112, knowledgeId: 1, refCode: "park-enterprise", url: "https://www.yzkj.com/faq/common-questions", contentLength: 5200, tokens: 2600, segmentMaxTokens: 500, retrievalCount: 11, status: 0, createTime: "2026-08-08 14:55:00" },
  { id: 201, knowledgeId: 2, refCode: "merchant-guide", description: "商户入驻条件、申请材料与审核流程说明手册", url: "https://oss.yzkj.com/kb/merchant/商户入驻手册.pdf", contentLength: 31200, tokens: 15620, segmentMaxTokens: 800, retrievalCount: 210, status: 0, createTime: "2026-06-21 13:55:00" },
  { id: 202, knowledgeId: 2, refCode: "merchant-guide", url: "https://oss.yzkj.com/kb/merchant/结算周期与对账规范.docx", contentLength: 12880, tokens: 6477, segmentMaxTokens: 800, retrievalCount: 96, status: 0, createTime: "2026-06-22 09:30:00" },
  { id: 203, knowledgeId: 2, refCode: "merchant-guide", url: "https://oss.yzkj.com/kb/merchant/营销活动申报流程.md", contentLength: 6400, tokens: 3210, segmentMaxTokens: 800, retrievalCount: 41, status: 1, createTime: "2026-07-01 15:08:00" },
  { id: 301, knowledgeId: 3, refCode: "product-manual", url: "https://oss.yzkj.com/kb/product/智能网关-G200-使用手册.pdf", contentLength: 88600, tokens: 44320, segmentMaxTokens: 500, retrievalCount: 64, status: 0, createTime: "2026-07-03 10:40:00" },
  { id: 302, knowledgeId: 3, refCode: "product-manual", url: "https://oss.yzkj.com/kb/product/常见问题-硬件篇.txt", contentLength: 15430, tokens: 7715, segmentMaxTokens: 500, retrievalCount: 19, status: 1, createTime: "2026-07-09 11:26:00" },
  { id: 401, knowledgeId: 4, refCode: "internal-policy", url: "https://oss.yzkj.com/kb/internal/员工考勤管理制度.docx", contentLength: 7300, tokens: 3650, segmentMaxTokens: 500, retrievalCount: 5, status: 0, createTime: "2026-07-19 16:42:00" },
  { id: 501, knowledgeId: 5, refCode: "test-kb", url: "https://oss.yzkj.com/kb/test/接口联调手册.pdf", contentLength: 3200, tokens: 1600, segmentMaxTokens: 500, retrievalCount: 3, status: 0, createTime: "2026-08-06 10:20:00" },
  { id: 502, knowledgeId: 5, refCode: "test-kb", url: "https://oss.yzkj.com/kb/test/测试用例集.md", contentLength: 2100, tokens: 1050, segmentMaxTokens: 500, retrievalCount: 1, status: 0, createTime: "2026-08-09 15:36:00" },
];

export default {
  name: "DocumentManagement",
  components: { PageOverlay, BizTypeSelect, SegmentManagement },
  props: {
    knowledge: {
      type: Object,
      required: true,
    },
  },
  data() {
    const validateSegTokens = (rule, value, callback) => {
      if (value === null || value === undefined || value === "") {
        return callback(new Error("请输入分片最大 Token 数"));
      }
      if (!Number.isInteger(value) || value < 100 || value > 4000) {
        return callback(new Error("请输入 100-4000 的整数"));
      }
      callback();
    };
    return {
      mockNotified: false,
      searchForm: {
        refCode: "",
      },
      docModalVisible: false,
      docConfirmLoading: false,
      docAddType: "local",
      uploadFile: null,
      fileError: false,
      // URL 校验手动维护（antd 对动态切换渲染的字段校验偶发不触发，切换来源后
      // 立即提交时空 URL 不报错）
      urlError: "",
      docForm: {
        url: "",
        description: "",
        refCode: undefined,
        segmentMaxTokens: 500,
      },
      docRules: {
        description: [{ max: 200, message: "文件描述最多200个字符", trigger: "blur" }],
        segmentMaxTokens: [{ required: true, validator: validateSegTokens, trigger: "change" }],
      },
      editModalVisible: false,
      editLoading: false,
      editConfirmLoading: false,
      editForm: {
        id: undefined,
        fileName: "",
        url: "",
        description: "",
        refCode: undefined,
        segmentMaxTokens: 500,
      },
      editRules: {
        description: [{ max: 200, message: "文件描述最多200个字符", trigger: "blur" }],
        segmentMaxTokens: [{ required: true, validator: validateSegTokens, trigger: "change" }],
      },
      detailDrawerVisible: false,
      detailLoading: false,
      detail: {},
      // 非空即打开分块管理覆盖层（值为文档行数据副本）
      segOverlayDoc: null,
      table: {
        loading: false,
        columns: [
          {
            title: "ID",
            dataIndex: "id",
            key: "id",
            width: 80,
          },
          {
            title: "文档",
            dataIndex: "url",
            key: "doc",
            width: 260,
            scopedSlots: { customRender: "doc" },
          },
          {
            title: "数据类型",
            dataIndex: "url",
            key: "dataType",
            width: 110,
            scopedSlots: { customRender: "dataType" },
          },
          {
            title: "引用编码",
            dataIndex: "refCode",
            key: "refCode",
            width: 130,
            scopedSlots: { customRender: "refCode" },
          },
          {
            title: "字符数",
            dataIndex: "contentLength",
            key: "contentLength",
            width: 100,
            scopedSlots: { customRender: "contentLength" },
          },
          {
            title: "Token 数",
            dataIndex: "tokens",
            key: "tokens",
            width: 100,
            scopedSlots: { customRender: "tokens" },
          },
          {
            title: "分片上限",
            dataIndex: "segmentMaxTokens",
            key: "segmentMaxTokens",
            width: 100,
          },
          {
            title: "召回次数",
            dataIndex: "retrievalCount",
            key: "retrievalCount",
            width: 100,
            scopedSlots: { customRender: "retrievalCount" },
          },
          {
            title: "状态",
            dataIndex: "status",
            key: "status",
            width: 100,
            scopedSlots: { customRender: "status" },
          },
          {
            title: "处理状态",
            dataIndex: "processStatus",
            key: "processStatus",
            width: 110,
            scopedSlots: { customRender: "processStatus" },
          },
          {
            title: "创建时间",
            dataIndex: "createTime",
            key: "createTime",
            width: 160,
            scopedSlots: { customRender: "createTime" },
          },
          {
            title: "操作",
            key: "action",
            width: 260,
            scopedSlots: { customRender: "action" },
          },
        ],
        dataInfo: [],
        pagination: {
          showSizeChanger: true,
          current: 1,
          pageSize: 10,
          total: 0,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 条`,
        },
      },
    };
  },
  computed: {
    statusConfig() {
      return KNOWLEDGE_STATUS_MAP[this.knowledge.status] || { label: "-", color: "default" };
    },
    thresholdText() {
      const value = this.knowledge.similarityThreshold;
      if (value === null || value === undefined || value === "") return "-";
      return Number(value).toFixed(2);
    },
    uploadFileExt() {
      if (!this.uploadFile) return "";
      const ext = (this.uploadFile.name.split(".").pop() || "").toLowerCase();
      return ext.slice(0, 4).toUpperCase() || "FILE";
    },
    uploadFileSizeText() {
      if (!this.uploadFile) return "";
      return `${(this.uploadFile.size / 1024).toFixed(1)} KB`;
    },
    detailExtBadge() {
      if (!this.detail || !this.detail.url) return "FILE";
      const name = String(this.detail.url).toLowerCase().split("/").pop().split("?")[0];
      const ext = name.includes(".") ? name.split(".").pop() : "";
      if (ext) return ext.slice(0, 4).toUpperCase();
      return dataTypeOfUrl(this.detail.url) === "link" ? "LINK" : "FILE";
    },
    detailStatusText() {
      const config = KNOWLEDGE_STATUS_MAP[this.detail.status];
      return config ? config.label : "-";
    },
  },
  mounted() {
    this.fetchDocList();
  },
  methods: {
    handleOverlayClose() {
      this.$emit("back");
    },
    isSuccess(res) {
      return res && [0, "0", 200, "200"].includes(res.code);
    },
    getStatusConfig(status) {
      return KNOWLEDGE_STATUS_MAP[status] || { label: "-", color: "default" };
    },
    getProcessStatusConfig(status) {
      return DOCUMENT_PROCESS_STATUS_MAP[status] || { label: "-", color: "default" };
    },
    formatNum(value) {
      if (value === null || value === undefined || value === "") return "-";
      return Number(value).toLocaleString("zh-CN");
    },
    formatDate(time) {
      if (!time) return "-";
      return String(time).split(" ")[0] || time;
    },
    formatTime(time) {
      if (!time) return "";
      const parts = String(time).split(" ");
      return parts[1] || "";
    },
    dataTypeConfig(url) {
      return DOC_DATA_TYPE_MAP[dataTypeOfUrl(url)] || DOC_DATA_TYPE_MAP.doc;
    },
    docFileName(url) {
      return docFileName(url);
    },
    search() {
      this.table.pagination.current = 1;
      this.fetchDocList();
    },
    resetSearchForm() {
      this.table.pagination.current = 1;
      this.table.pagination.pageSize = 10;
      this.searchForm = this.$options.data.call(this).searchForm;
      this.fetchDocList();
    },
    pageChange(option) {
      this.table.pagination.current = option.current;
      this.table.pagination.pageSize = option.pageSize;
      this.fetchDocList();
    },
    async fetchDocList() {
      if (USE_MOCK) {
        this.applyMockList();
        return;
      }
      this.table.loading = true;
      try {
        const params = {
          knowledgeId: this.knowledge.id,
          pageNo: this.table.pagination.current,
          pageSize: this.table.pagination.pageSize,
        };
        if (this.searchForm.refCode) {
          params.refCode = this.searchForm.refCode;
        }
        const res = await getDocumentPage(params);
        if (this.isSuccess(res)) {
          if (Array.isArray(res.data)) {
            this.table.dataInfo = res.data;
            this.table.pagination.total = res.data.length;
          } else {
            this.table.dataInfo = res.data?.list || res.data?.records || [];
            this.table.pagination.total = res.data?.total || 0;
          }
        } else {
          this.$message.error(res?.msg || "获取文档列表失败");
        }
      } catch {
        this.$message.error("获取文档列表失败");
      } finally {
        this.table.loading = false;
      }
    },
    // 临时 Mock：预览样式用，联调后删除本方法及调用处
    applyMockList() {
      const keyword = (this.searchForm.refCode || "").trim();
      const filtered = MOCK_DOC_LIST.filter(
        (item) =>
          String(item.knowledgeId) === String(this.knowledge.id) &&
          (!keyword || item.refCode.includes(keyword))
      );
      const { current, pageSize } = this.table.pagination;
      this.table.dataInfo = filtered.slice((current - 1) * pageSize, current * pageSize);
      this.table.pagination.total = filtered.length;
      if (!this.mockNotified) {
        this.mockNotified = true;
        this.$message.warning("预览模式：当前展示 Mock 数据");
      }
    },
    // ---- 新建文档 ----
    openDocAddModal() {
      this.resetDocForm();
      this.docModalVisible = true;
    },
    resetDocForm() {
      this.docAddType = "local";
      this.uploadFile = null;
      this.fileError = false;
      this.urlError = "";
      this.docForm = {
        url: "",
        description: "",
        refCode: undefined,
        segmentMaxTokens: 500,
      };
      // 业务类型组件内部状态需显式复位（value 未变化时 watch 不触发）
      if (this.$refs.docBizType) {
        this.$refs.docBizType.reset();
      }
      this.$nextTick(() => {
        if (this.$refs.docForm) {
          this.$refs.docForm.clearValidate();
        }
      });
    },
    handleDocAddCancel() {
      this.docModalVisible = false;
      this.resetDocForm();
    },
    handleSourceTypeChange() {
      this.fileError = false;
      this.urlError = "";
      this.$nextTick(() => {
        if (this.$refs.docForm) {
          this.$refs.docForm.clearValidate();
        }
      });
    },
    beforeDocUpload(file) {
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      if (!DOC_UPLOAD_EXTS.includes(ext)) {
        this.$message.error(`仅支持 ${DOC_UPLOAD_EXTS.join(" / ")} 格式`);
        return false;
      }
      if (file.size > DOC_UPLOAD_MAX_MB * 1024 * 1024) {
        this.$message.error(`文件大小不能超过 ${DOC_UPLOAD_MAX_MB}MB`);
        return false;
      }
      this.uploadFile = file;
      this.fileError = false;
      // 返回 false 阻止自动上传，提交时随表单一起走 multipart
      return false;
    },
    removeUploadFile() {
      this.uploadFile = null;
    },
    buildUploadFormData(base) {
      const formData = new FormData();
      formData.append("file", this.uploadFile);
      Object.keys(base).forEach((key) => {
        if (base[key] !== undefined && base[key] !== null) {
          formData.append(key, base[key]);
        }
      });
      return formData;
    },
    handleDocAddOk() {
      // 文件/URL 的手动校验先置位（与表单、业务类型校验同时展示，避免被前置
      // return 拦住导致“请选择要上传的文件”在业务类型未选时不出现）
      if (this.docAddType === "local") {
        this.fileError = !this.uploadFile;
        this.urlError = "";
      } else {
        this.fileError = false;
        const url = (this.docForm.url || "").trim();
        this.urlError = !url
          ? "请输入文件 URL"
          : /^https?:\/\//.test(url)
            ? ""
            : "请输入合法的 http(s) 文件地址";
      }
      this.$refs.docForm.validate(async (valid) => {
        // 业务类型两段式校验由组件内部表单项渲染行内错误
        const bizValid = this.$refs.docBizType ? await this.$refs.docBizType.validate() : true;
        if (!valid || !bizValid || this.urlError || this.fileError) return;
        this.docConfirmLoading = true;
        try {
          const isLocal = this.docAddType === "local";
          if (USE_MOCK) {
            this.applyMockCreate(isLocal);
            return;
          }
          const base = {
            knowledgeId: this.knowledge.id,
            refCode: this.docForm.refCode,
            description: this.docForm.description,
            segmentMaxTokens: this.docForm.segmentMaxTokens,
          };
          const res = isLocal
            ? await uploadDocument(this.buildUploadFormData(base))
            : await createDocument({ ...base, url: this.docForm.url.trim() });
          if (this.isSuccess(res)) {
            this.$message.success(
              res.msg || (isLocal ? "上传成功，已进入解析队列" : "文档创建成功")
            );
            this.handleDocAddCancel();
            this.search();
          } else {
            this.$message.error(res?.msg || "保存失败");
          }
        } catch {
          this.$message.error("保存失败");
        } finally {
          this.docConfirmLoading = false;
        }
      });
    },
    // 临时 Mock：预览新建文档交互，联调后删除
    applyMockCreate(isLocal) {
      const newId = Math.max(100, ...MOCK_DOC_LIST.map((item) => item.id)) + 1;
      MOCK_DOC_LIST.unshift({
        id: newId,
        knowledgeId: this.knowledge.id,
        refCode: this.docForm.refCode,
        description: this.docForm.description,
        url: isLocal
          ? "https://oss.yzkj.com/kb/upload/" + encodeURIComponent(this.uploadFile.name)
          : this.docForm.url.trim(),
        contentLength: isLocal ? Math.round(this.uploadFile.size * 0.9) : 0,
        tokens: isLocal ? Math.round(this.uploadFile.size * 0.5) : 0,
        segmentMaxTokens: this.docForm.segmentMaxTokens,
        retrievalCount: 0,
        status: 0,
        createTime: new Date()
          .toLocaleString("zh-CN", { hour12: false })
          .replace(/\//g, "-"),
      });
      this.$message.success(isLocal ? "上传成功，已进入解析队列（Mock）" : "文档创建成功（Mock）");
      this.docConfirmLoading = false;
      this.handleDocAddCancel();
      this.search();
    },
    // ---- 编辑文档：详情接口回显，仅 refCode/描述/分片上限可改 ----
    resetEditForm() {
      this.editForm = {
        id: undefined,
        fileName: "",
        url: "",
        description: "",
        refCode: undefined,
        segmentMaxTokens: 500,
      };
      // 业务类型组件内部状态需显式复位（value 未变化时 watch 不触发）
      if (this.$refs.editBizType) {
        this.$refs.editBizType.reset();
      }
      this.$nextTick(() => {
        if (this.$refs.editForm) {
          this.$refs.editForm.clearValidate();
        }
      });
    },
    fillEditFormFromDetail(data) {
      this.editForm = {
        id: data.id,
        fileName: docFileName(data.url),
        url: data.url || "",
        description: data.description || "",
        refCode: data.refCode || undefined,
        segmentMaxTokens: data.segmentMaxTokens != null ? data.segmentMaxTokens : 500,
      };
    },
    async openDocEditModal(record) {
      this.resetEditForm();
      this.editModalVisible = true;
      this.editLoading = true;
      try {
        const res = await getDocumentDetail(record.id);
        if (this.isSuccess(res) && res.data) {
          this.fillEditFormFromDetail(res.data);
        } else {
          this.$message.error(res?.msg || "获取详情失败");
          this.editModalVisible = false;
        }
      } catch {
        this.$message.error("获取详情失败");
        this.editModalVisible = false;
      } finally {
        this.editLoading = false;
      }
    },
    handleEditCancel() {
      this.editModalVisible = false;
      this.resetEditForm();
    },
    handleEditOk() {
      this.$refs.editForm.validate(async (valid) => {
        // 业务类型两段式校验由组件内部表单项渲染行内错误
        const bizValid = this.$refs.editBizType ? await this.$refs.editBizType.validate() : true;
        if (!valid || !bizValid) return;
        this.editConfirmLoading = true;
        try {
          const res = await updateDocument({
            id: this.editForm.id,
            refCode: this.editForm.refCode,
            // 空描述一并提交，保证可以清空
            description: this.editForm.description,
            segmentMaxTokens: this.editForm.segmentMaxTokens,
          });
          if (this.isSuccess(res)) {
            this.$message.success(res.msg || "保存成功");
            this.handleEditCancel();
            this.fetchDocList();
          } else {
            this.$message.error(res?.msg || "保存失败");
          }
        } catch {
          this.$message.error("保存失败");
        } finally {
          this.editConfirmLoading = false;
        }
      });
    },
    // ---- 文档详情抽屉 ----
    isHttpUrl(url) {
      return /^https?:\/\//.test(String(url || ""));
    },
    fillDetail(data) {
      this.detail = {
        ...data,
        fileName: docFileName(data.url),
      };
    },
    async openDetailDrawer(record) {
      this.detail = {};
      this.detailDrawerVisible = true;
      this.detailLoading = true;
      try {
        const res = await getDocumentDetail(record.id);
        if (this.isSuccess(res) && res.data) {
          this.fillDetail(res.data);
        } else {
          // 详情接口异常时退化为行数据展示
          this.fillDetail(record);
          this.$message.error(res?.msg || "获取详情失败");
        }
      } catch {
        this.fillDetail(record);
        this.$message.error("获取详情失败");
      } finally {
        this.detailLoading = false;
      }
    },
    closeDetailDrawer() {
      this.detailDrawerVisible = false;
      this.detail = {};
    },
    // ---- 启停 / 删除 ----
    handleStatusChange(record, checked) {
      const status = checked ? 0 : 1;
      const actionText = checked ? "启用" : "停用";
      const that = this;
      this.$confirm({
        title: `确认${actionText}文档「${this.docFileName(record.url)}」？`,
        content: checked ? "" : "停用后该文档及其分块将不参与检索。",
        onOk() {
          return updateDocumentStatus({ id: record.id, status })
            .then((res) => {
              if (that.isSuccess(res)) {
                that.$message.success(res.msg || `${actionText}成功`);
                that.fetchDocList();
              } else {
                that.$message.error(res?.msg || `${actionText}失败`);
              }
            })
            .catch(() => {
              that.$message.error(`${actionText}失败`);
            });
        },
      });
    },
    handleDelete(record) {
      const that = this;
      this.$confirm({
        title: `确认删除文档「${this.docFileName(record.url)}」？`,
        content: "删除后其向量数据将同步清除，不可恢复。",
        okType: "danger",
        onOk() {
          return deleteDocument(record.id)
            .then((res) => {
              if (that.isSuccess(res)) {
                that.$message.success(res.msg || "删除成功");
                // 当前页删到最后一条时回退一页，避免停留在空页
                if (that.table.dataInfo.length === 1 && that.table.pagination.current > 1) {
                  that.table.pagination.current -= 1;
                }
                that.fetchDocList();
              } else {
                that.$message.error(res?.msg || "删除失败");
              }
            })
            .catch(() => {
              that.$message.error("删除失败");
            });
        },
      });
    },
    openSegView(record) {
      this.segOverlayDoc = { ...record };
    },
    closeSegView() {
      this.segOverlayDoc = null;
      // 返回时刷新文档列表与统计口径（召回数可能变化）
      this.fetchDocList();
    },
    handleEdit(record) {
      this.openDocEditModal(record);
    },
    handleDetail(record) {
      this.openDetailDrawer(record);
    },
  },
};
</script>

<style scoped lang="scss">
.document-management {
  // 操作列吸右：用原生 sticky 替代 antd fixed 右列——fixed 列是独立表格图层，
  // 多行单元格（文档名+URL 两行）下会行高错位、竖边框叠成双线；sticky 单表格渲染无此问题
  ::v-deep .ant-table {
    th:last-child,
    td:last-child {
      position: sticky;
      right: 0;
      z-index: 1;
      background: #fff;
      // 吸右覆盖左侧内容时，分隔线由本列左边框绘制
      border-left: 1px solid #e8e8e8;
    }

    // 分隔线统一由最后一列绘制，去掉倒数第二列的右边框避免双线
    th:nth-last-child(2),
    td:nth-last-child(2) {
      border-right: none;
    }

    th:last-child {
      z-index: 2;
      background: #fafafa;
    }

    tbody tr:hover td:last-child {
      background: #e6f7ff;
    }
  }

  .kb-head {
    display: flex;
    align-items: center;
    gap: 16px;
    padding: 18px 20px;
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }

  .kb-ico {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    background: linear-gradient(135deg, #4f7cff, #8a5cf6);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
  }

  .kb-name {
    font-size: 16px;
    font-weight: 700;
    color: rgba(0, 0, 0, 0.85);
  }

  .kb-desc {
    font-size: 12.5px;
    color: rgba(0, 0, 0, 0.45);
    margin-top: 4px;
  }

  .kb-cfg {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    margin-top: 7px;

    b {
      color: rgba(0, 0, 0, 0.65);
      font-weight: 600;
    }
  }

  .list-card {
    background: #fff;
    border: 1px solid #f0f0f0;
    border-radius: 4px;
    padding: 16px 18px;
  }

  .toolbar {
    padding-top: 15px;
  }
}

/* 以下样式作用于弹窗/抽屉的内部内容：a-modal / a-drawer 经 portal 渲染到
   body 下，scoped 样式若嵌套在 .document-management 内会因祖先选择器
   匹配不到而整体失效，必须放在根层级（元素自身仍带本组件的 scoped 标记） */
.field-hint {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  line-height: 1.5;
  margin-top: 5px;
}

.file-chip {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  padding: 10px 12px;
  background: #f4f7fd;
  border: 1px solid #e8e8e8;
  border-radius: 8px;

  .file-chip__type {
    width: 38px;
    height: 26px;
    line-height: 26px;
    text-align: center;
    border-radius: 5px;
    background: #3b6cf6;
    color: #fff;
    font-size: 10px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .file-chip__name {
    flex: 1;
    min-width: 0;
    font-weight: 600;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .file-chip__size {
    font-size: 12px;
    color: rgba(0, 0, 0, 0.45);
    flex-shrink: 0;
  }

  .file-chip__remove {
    cursor: pointer;
    color: #ff4d4f;
    font-size: 14px;
    flex-shrink: 0;

    &:hover {
      color: #ff7875;
    }
  }
}

.file-error {
  margin-top: 5px;
  font-size: 12px;
  color: #f5222d;
}

.detail-drawer {
  .detail-drawer__mask {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 999;
    background: rgba(15, 23, 42, 0.45);
  }

  .detail-drawer__panel {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    width: 720px;
    max-width: 92vw;
    z-index: 1000;
    background: #fff;
    box-shadow: -12px 0 40px rgba(15, 23, 42, 0.14);
    display: flex;
    flex-direction: column;
    animation: detail-drawer-in 0.22s ease;
  }

  .detail-drawer__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    flex-shrink: 0;
    padding: 0 24px;
    border-bottom: 1px solid #f0f0f0;

    .detail-drawer__title {
      font-size: 15px;
      font-weight: 600;
      color: rgba(0, 0, 0, 0.85);
    }

    .detail-drawer__close {
      cursor: pointer;
      color: rgba(0, 0, 0, 0.45);
      font-size: 18px;
      padding: 4px;

      &:hover {
        color: rgba(0, 0, 0, 0.85);
      }
    }
  }

  .detail-drawer__body {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 20px 24px;
  }
}

@keyframes detail-drawer-in {
  from {
    transform: translateX(40px);
    opacity: 0;
  }

  to {
    transform: none;
    opacity: 1;
  }
}

.detail-file {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;

  .detail-file__badge {
    width: 40px;
    height: 40px;
    line-height: 40px;
    text-align: center;
    border-radius: 8px;
    background: #3b6cf6;
    color: #fff;
    font-size: 11px;
    font-weight: 700;
    flex-shrink: 0;
  }

  .detail-file__info {
    min-width: 0;
  }
}

.kv-list {
  margin-bottom: 18px;

  .kv {
    display: flex;
    padding: 8px 0;
    border-bottom: 1px solid #f0f3f8;
    font-size: 13px;

    .k {
      width: 90px;
      flex-shrink: 0;
      color: rgba(0, 0, 0, 0.45);
    }

    .v {
      flex: 1;
      min-width: 0;
      color: rgba(0, 0, 0, 0.75);
      word-break: break-all;
    }
  }
}

.detail-content-title {
  font-size: 13px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  margin-bottom: 8px;
}

.detail-content-box {
  padding: 14px;
  background: #f7f9fc;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  max-height: 340px;
  overflow-y: auto;
  font-size: 13px;
  line-height: 1.8;
  white-space: pre-wrap;
  word-break: break-all;
  color: #3b4557;
}

// 表格与详情抽屉共用
.cell-main {
  font-weight: 600;
  font-size: 13.5px;
}

.cell-sub {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-top: 3px;
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.num {
  font-variant-numeric: tabular-nums;
}

.document-management {
  .time-cell {
    line-height: 1.4;
  }

  .time-sub {
    color: rgba(0, 0, 0, 0.45);
    font-size: 12px;
  }

  .action {
    display: flex;
    align-items: center;
    gap: 8px;

    // 清掉 antd 链接按钮默认 15px 内边距，避免操作内容超出列宽压到左侧列
    ::v-deep .ant-btn {
      padding: 0;
    }
  }

  .delete-btn {
    color: #ff4d4f;
  }

  .ref-code-tag {
    font-family: monospace;
  }
}
</style>
